import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Bell, CalendarDays, ChevronRight, ClipboardList, ExternalLink, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, ago, download, errMsg } from "@/lib/api";
import { Empty, Loading, PageHeader } from "@/components/common";
const STATUSES = ["saved", "preparing", "ready", "applied", "interviewing", "offer", "rejected", "withdrawn"];
const COLS = [["Preparing", ["saved", "preparing", "ready"]], ["Applied", ["applied"]], ["Interviewing", ["interviewing"]], ["Outcome", ["offer", "rejected", "withdrawn"]]];
function Detail({
  a,
  onChange
}) {
  const [proof, setProof] = useState({
    kind: "confirmation_page",
    url: "",
    text: "",
    request_key: crypto.randomUUID()
  });
  const [savingProof, setSavingProof] = useState(false);
  const [note, setNote] = useState("");
  const [rem, setRem] = useState({
    at: "",
    text: ""
  });
  const call = async fn => {
    try {
      onChange((await fn()).data);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  return <div className="mt-6 space-y-6 text-sm">
      <Button asChild className="w-full" data-testid="tracker-apply-link"><a href={a.apply_url} target="_blank" rel="noopener noreferrer">Apply on employer site <ExternalLink className="ml-2 h-4 w-4" /></a></Button>
      <div className="space-y-1.5"><p className="eyebrow">Status</p>
        <Select value={a.status} onValueChange={s => call(() => api.patch(`/applications/${a.id}/status`, {
        status: s
      }))}>
          <SelectTrigger data-testid="tracker-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Status is reported by you, not verified by an employer. Mark "applied" after submitting on their site.</p></div>
      <div><p className="eyebrow mb-2">History</p><ol className="space-y-1.5 border-l pl-4" data-testid="tracker-history">
        {[...a.history].reverse().map((h, i) => <li key={i}><span className="font-medium capitalize">{h.status}</span> <span className="text-muted-foreground">· {ago(h.at)}{h.note ? ` · ${h.note}` : ""}</span></li>)}</ol></div>
      <section><h3 className="eyebrow mb-2">Submission evidence</h3><p className="mb-3 text-xs text-muted-foreground">Your reported confirmation/reference. Saving evidence does not verify employer receipt or change status. URL queries and fragments are removed.</p>{(a.evidence || []).map(item => <div key={item.id} className="mb-3 rounded-lg border p-3 text-xs"><p className="font-medium capitalize">{item.kind.replaceAll("_", " ")}</p><p className="mt-2 whitespace-pre-wrap break-words">{item.text}</p>{item.url && <p className="mt-2 break-all text-muted-foreground">{item.url}</p>}<p className="mt-2 text-muted-foreground">User reported · {ago(item.at)}</p></div>)}<form className="space-y-3" onSubmit={async e => {
        e.preventDefault();
        setSavingProof(true);
        try {
          if (await call(() => api.post(`/copilot/applications/${a.id}/evidence`, proof))) setProof({
            kind: "confirmation_page",
            url: "",
            text: "",
            request_key: crypto.randomUUID()
          });
        } finally {
          setSavingProof(false);
        }
      }}><fieldset disabled={savingProof} className="space-y-3"><select aria-label="Evidence type" value={proof.kind} onChange={e => setProof({
            ...proof,
            kind: e.target.value,
            request_key: crypto.randomUUID()
          })} className="h-10 w-full rounded-md border bg-background px-3 text-sm">{["confirmation_page", "reference_number", "user_note"].map(kind => <option key={kind} value={kind}>{kind.replaceAll("_", " ")}</option>)}</select><Input aria-label="Confirmation URL" type="url" maxLength={2000} placeholder="Confirmation HTTPS URL (optional)" value={proof.url} onChange={e => setProof({
            ...proof,
            url: e.target.value,
            request_key: crypto.randomUUID()
          })} /><Input aria-label="Confirmation details" required maxLength={2000} placeholder="Confirmation text or application reference" value={proof.text} onChange={e => setProof({
            ...proof,
            text: e.target.value,
            request_key: crypto.randomUUID()
          })} /><Button variant="outline" disabled={savingProof} type="submit">{savingProof ? "Saving…" : "Save reported evidence"}</Button></fieldset></form></section>
      <div><p className="eyebrow mb-2">Notes</p>
        {a.notes.map(n => <p key={n.id} className="mb-2 rounded-md bg-muted p-2">{n.text}<span className="block text-[11px] text-muted-foreground">{ago(n.at)}</span></p>)}
        <form className="flex gap-2" onSubmit={async e => {
        e.preventDefault();
        if (await call(() => api.post(`/applications/${a.id}/notes`, {
          text: note
        }))) setNote("");
      }}>
          <Input required placeholder="Add a note" value={note} onChange={e => setNote(e.target.value)} data-testid="tracker-note-input" /><Button data-testid="tracker-note-submit">Add</Button></form></div>
      <div><p className="eyebrow mb-2">Reminders</p>
        {a.reminders.map(r => <div key={r.id} className="mb-2 flex items-center justify-between gap-2 rounded-md border p-2">
            <span className={r.done ? "line-through opacity-60" : ""}><Bell className="mr-1 inline h-3 w-3" />{r.text} · {new Date(r.at).toLocaleString()}</span>
            {!r.done && <Button size="sm" variant="ghost" onClick={() => call(() => api.post(`/applications/${a.id}/reminders/${r.id}/done`))} data-testid={`reminder-done-${r.id}`}>Done</Button>}
          </div>)}
        <form className="grid gap-2" onSubmit={async e => {
        e.preventDefault();
        if (await call(() => api.post(`/applications/${a.id}/reminders`, {
          at: new Date(rem.at).toISOString(),
          text: rem.text
        }))) setRem({
          at: "",
          text: ""
        });
      }}>
          <Input type="datetime-local" required value={rem.at} onChange={e => setRem({
          ...rem,
          at: e.target.value
        })} data-testid="tracker-reminder-at" aria-label="Reminder time" />
          <Input required placeholder="e.g. Follow up with recruiter" value={rem.text} onChange={e => setRem({
          ...rem,
          text: e.target.value
        })} data-testid="tracker-reminder-text" />
          <Button variant="outline" data-testid="tracker-reminder-submit">Add reminder</Button></form></div>
    </div>;
}
export default function Tracker() {
  const [apps, setApps] = useState(null);
  const [sel, setSel] = useState(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/applications").then(r => setApps(r.data)).catch(e => setError(errMsg(e)));
  }, []);
  const update = a => {
    setApps(xs => xs.map(x => x.id === a.id ? a : x));
    setSel(a);
  };
  const visible = apps?.filter(a => `${a.job_title} ${a.company}`.toLowerCase().includes(query.trim().toLowerCase())) || [];
  return <div className="space-y-6">
      <PageHeader eyebrow="Every opportunity, one clear view" title="Your application pipeline" testId="tracker-heading"><Button variant="outline" onClick={() => download("/copilot/tracker.csv", "ajobf-applications.csv").catch(e => toast.error(errMsg(e)))}><Download size={15} />Export CSV</Button><Button asChild variant="outline"><Link to="/interviews"><CalendarDays size={15} />Interview planner</Link></Button><Button asChild><Link to="/jobs">Find opportunities<ChevronRight size={15} /></Link></Button></PageHeader>
      <div className="panel flex flex-wrap items-center justify-between gap-3 p-4"><div className="relative min-w-48 flex-1"><Search className="absolute left-3 top-2.5 text-muted-foreground" size={16} /><Input aria-label="Search applications" placeholder="Search role or company" value={query} onChange={e => setQuery(e.target.value)} className="max-w-md pl-9" /></div><p className="text-xs text-muted-foreground">{apps?.length || 0} tracked opportunities · User-reported statuses</p></div>
      {error && <p role="alert" className="panel p-4 text-sm text-destructive">{error}</p>}
      {!apps ? !error && <Loading /> : apps.length === 0 ? <Empty icon={ClipboardList} title="No saved jobs yet" testId="tracker-empty">Save a job from its detail page to track it here.</Empty> : <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
          {COLS.map(([title, sts]) => <section key={title} className="min-h-64 rounded-xl border border-dashed bg-muted/40 p-3" data-testid={`tracker-column-${title.toLowerCase()}`}>
              <p className="mb-4 flex items-center justify-between px-1 text-xs font-semibold">{title}<span className="rounded-md border bg-card px-2 py-0.5 text-[10px] text-muted-foreground">{visible.filter(a => sts.includes(a.status)).length}</span></p>
              {!visible.some(a => sts.includes(a.status)) && <p className="rounded-lg border border-dashed p-5 text-center text-xs text-muted-foreground">No opportunities here yet</p>}
              {visible.filter(a => sts.includes(a.status)).map(a => <button key={a.id} onClick={() => setSel(a)} data-testid={`tracker-card-${a.id}`} className="panel mb-3 w-full p-4 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <p className="font-medium">{a.job_title}</p><p className="text-xs text-muted-foreground">{a.company}</p>
                  <p className="mt-2 text-xs"><span className="rounded-full bg-muted px-2 py-0.5 capitalize">{a.status}</span> <span className="text-muted-foreground">· {ago(a.updated_at)}</span></p>
                </button>)}
            </section>)}
        </div>}
      <Sheet open={!!sel} onOpenChange={o => !o && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="tracker-detail-sheet">
          {sel && <><SheetHeader><SheetTitle>{sel.job_title}</SheetTitle><SheetDescription>Manage your status, notes and follow-up reminders.</SheetDescription><Link to={`/jobs/${sel.job_id}`} className="text-sm text-muted-foreground underline">{sel.company} · open job</Link></SheetHeader>
            <Detail key={sel.id} a={sel} onChange={update} /></>}
        </SheetContent>
      </Sheet>
    </div>;
}
